/**
 * Risk Profile Wizard Configuration
 * Configuration for the generic wizard framework
 */

import { lazy } from 'react';
import { WizardConfig, WizardTheme, WizardStep } from '../wizard/types';
import { CompleteRiskProfile } from './schemas/riskProfileSchema';
import riskProfileService from '../../services/riskProfileService';
import { Download } from 'lucide-react';
import {
  User,
  GraduationCap,
  DollarSign,
  Target,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  FileCheck,
  FileSignature,
  Calendar
} from 'lucide-react';

// Lazy-loaded step components
const PersonalInfoStep = lazy(() => import('./steps/PersonalInfoStep'));
const KnowledgeExperienceStep = lazy(() => import('./steps/KnowledgeExperienceStep'));
const FinancialSituationStep = lazy(() => import('./steps/FinancialSituationStep'));
const InvestmentObjectivesStep = lazy(() => import('./steps/InvestmentObjectivesStep'));
const RiskToleranceStep = lazy(() => import('./steps/RiskToleranceStep'));
const DeterminedProfileStep = lazy(() => import('./steps/DeterminedProfileStep'));
const RecommendedStrategyStep = lazy(() => import('./steps/RecommendedStrategyStep'));
const DeclarationsStep = lazy(() => import('./steps/DeclarationsStep'));
const SignaturesStep = lazy(() => import('./steps/SignaturesStep'));
const PeriodicReviewStep = lazy(() => import('./steps/PeriodicReviewStep'));

/**
 * Risk Profile Step Definitions
 * All 10 sections from the FinSA/MiFID II compliant questionnaire
 */
export const RISK_PROFILE_STEPS: WizardStep<CompleteRiskProfile>[] = [
  {
    id: 1,
    name: 'Personal Information',
    shortName: 'Personal Info',
    section: 'section1',
    component: PersonalInfoStep,
    icon: User,
    description: 'Basic personal and contact information'
  },
  {
    id: 2,
    name: 'Investment Knowledge & Experience',
    shortName: 'Knowledge',
    section: 'section2',
    component: KnowledgeExperienceStep,
    icon: GraduationCap,
    description: 'Education and investment experience'
  },
  {
    id: 3,
    name: 'Financial Situation',
    shortName: 'Financial',
    section: 'section3',
    component: FinancialSituationStep,
    icon: DollarSign,
    description: 'Income, assets, liabilities, and expenses'
  },
  {
    id: 4,
    name: 'Investment Objectives',
    shortName: 'Objectives',
    section: 'section4',
    component: InvestmentObjectivesStep,
    icon: Target,
    description: 'Time horizon, purposes, and return expectations'
  },
  {
    id: 5,
    name: 'Risk Tolerance',
    shortName: 'Risk Tolerance',
    section: 'section5',
    component: RiskToleranceStep,
    icon: AlertTriangle,
    description: 'Risk attitude and investment restrictions'
  },
  {
    id: 6,
    name: 'Determined Risk Profile',
    shortName: 'Risk Profile',
    section: 'section6',
    component: DeterminedProfileStep,
    icon: BarChart3,
    description: 'System-calculated or advisor-filled risk profile'
  },
  {
    id: 7,
    name: 'Recommended Investment Strategy',
    shortName: 'Strategy',
    section: 'section7',
    component: RecommendedStrategyStep,
    icon: TrendingUp,
    description: 'Investment strategy and asset allocation'
  },
  {
    id: 8,
    name: 'Declarations & Acknowledgments',
    shortName: 'Declarations',
    section: 'section8',
    component: DeclarationsStep,
    icon: FileCheck,
    description: 'Client acknowledgments and agreements'
  },
  {
    id: 9,
    name: 'Signatures',
    shortName: 'Signatures',
    section: 'section9',
    component: SignaturesStep,
    icon: FileSignature,
    description: 'Digital signatures from client and advisor'
  },
  {
    id: 10,
    name: 'Periodic Review',
    shortName: 'Review',
    section: 'section10',
    component: PeriodicReviewStep,
    icon: Calendar,
    description: 'Review frequency and triggers'
  }
];

/**
 * Kielis Theme for Risk Profile
 * Same theme as KYC for consistency
 */
export const RISK_PROFILE_THEME: WizardTheme = {
  // Header
  headerBg: 'bg-kielis-navy',
  headerText: 'text-white',
  accentColor: 'bg-kielis-gold',

  // Layout
  sidebarBg: 'bg-white',
  contentBg: 'bg-kielis-glacier',

  // Progress indicator
  progressBarColor: 'bg-kielis-gold',
  progressDotColor: 'bg-white',
  progressDotRing: 'ring-kielis-gold',

  // Buttons
  primaryButton: {
    bg: 'bg-kielis-navy',
    text: 'text-white',
    hover: 'hover:bg-opacity-90',
    focus: 'focus:ring-2 focus:ring-kielis-gold',
    border: ''
  },
  secondaryButton: {
    bg: 'bg-white',
    text: 'text-gray-700',
    hover: 'hover:bg-gray-50',
    focus: 'focus:ring-2 focus:ring-kielis-gold',
    border: 'border-2 border-gray-300'
  },

  // Sidebar step states
  activeStepBg: 'bg-kielis-navy',
  activeStepText: 'text-white',
  completedStepBg: 'bg-white',
  completedStepBorder: 'border-green-200',
  upcomingStepBg: 'bg-white',
  upcomingStepText: 'text-gray-600'
};

/**
 * Create Risk Profile Wizard Configuration
 * Factory function with PDF download actions
 *
 * @param clientId - Client ID for the risk profile
 * @param onDownloadBlankPDF - Handler for blank PDF generation
 * @param onDownloadFilledPDF - Handler for filled PDF generation
 * @returns WizardConfig for Risk Profile
 */
export function createRiskProfileWizardConfig(
  clientId: string,
  onDownloadBlankPDF: () => Promise<void>,
  onDownloadFilledPDF: () => Promise<void>
): WizardConfig<CompleteRiskProfile> {
  return {
    formId: 'risk-profile',
    formTitle: 'Investment Risk Profile',
    steps: RISK_PROFILE_STEPS,
    theme: RISK_PROFILE_THEME,
    features: {
      autoSave: true,
      pdfDownload: true,
      extractionReview: true,
      reviewMode: false,  // Risk Profile doesn't use review mode like KYC
      progressIndicator: true,
      stepValidation: false  // Set to false for now, can enable later with Zod validation
    },
    headerActions: [
      {
        id: 'download-blank-pdf',
        label: 'Generate PDF',
        icon: Download,
        onClick: onDownloadBlankPDF,
        variant: 'secondary',
        disabled: false
      },
      {
        id: 'download-filled-pdf',
        label: 'Download PDF',
        icon: Download,
        onClick: onDownloadFilledPDF,
        variant: 'primary',
        disabled: false
      }
    ],
    validateStep: async (_step: number, _data: Partial<CompleteRiskProfile>) => {
      // Risk Profile-specific validation logic
      // For now, allow all steps (can be enhanced with Zod validation later)
      return true;
    },
    onSave: async (data: Partial<CompleteRiskProfile>) => {
      // Save progress to backend
      await riskProfileService.save(clientId, data);

      // Also save to localStorage
      const storageKey = `risk_profile_draft_${clientId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        formData: data,
        savedAt: new Date().toISOString()
      }));

      console.log('[Risk Profile] Progress saved');
    },
    onComplete: async (data: CompleteRiskProfile) => {
      // Submit Risk Profile to backend
      await riskProfileService.submit(clientId, data);

      // Clear localStorage draft
      const storageKey = `risk_profile_draft_${clientId}`;
      localStorage.removeItem(storageKey);

      console.log('[Risk Profile] Form submitted successfully');
    }
  };
}
